import isNullOrEmpty from "../helpers/isNullOrEmpty";
import MessageParserError from "./MessageParserError";
import {ParserArgument} from "./ParserArgument";
import {ParserArgumentType} from "./ParserArgumentType";

const _splitRegex: RegExp = /\r?\n/;
const _types: ParserArgumentType[] = ["number", "datetime", "date", "time", "ordinal", "duration", "spellout", "plural", "selectordinal", "select", "list"];

function errorMessage(expected: string | null, found: string): string {
	return expected == null ? `Unexpected ´${found}´ found` : `Was expecting ´${expected}´, but found ´${found}´`;
}

function isDigit(c: string): boolean {
	return c === "0" || c === "1" || c === "2" || c === "3" || c === "4" || c === "5" || c === "6" || c === "7" || c === "8" || c === "9";
}

function isWhitespace(character: string): boolean {
	if (character == null) {
		return true;
	}

	const code = (character.charCodeAt(0));
	return code >= 9 && code <= 13 || code === 32 || code === 133 || code === 160 || code === 6158 || code >= 8192 && code <= 8205 || code === 8232 || code === 8233 || code === 8239 || code === 8287 || code === 8288 || code === 12288 || code === 65279;
}

interface ParserState {
	length: number;
	pattern: string;
	index: number;
}

function containsAtIndex(state: ParserState, value: string): boolean {
	if (isNullOrEmpty(value)) {
		return false;
	}
	const l = value.length;
	const length = state.length;
	let index = 0;
	let patternIndex = state.index;
	while (index < l && patternIndex < length) {
		if (value[index] !== state.pattern[patternIndex]) {
			return false;
		}
		++index;
		++patternIndex;
	}
	//si no son iguales, es porque el valor excede en la posición actual
	//al largo total del pattern.
	return index === l;
}

function parseArgId(state: ParserState): string {
	skipWhitespace(state);
	let builder = "";
	while (state.index < state.length) {
		const c = state.pattern[state.index];
		if (c === "{" || c === "#") {
			throwExpected(state, "argument id", null, null);
		}
		if (c === "}" || c === "," || isWhitespace(c)) {
			break;
		}
		builder += c;
		++state.index;
	}
	if (builder.length === 0) {
		throwExpected(state, "argument id", null, null);
	}
	skipWhitespace(state);
	return builder;
}

function parseArgType(state: ParserState): ParserArgumentType {
	skipWhitespace(state);
	let argType = null;
	for (const type of _types) {
		if (!containsAtIndex(state, type)) {
			continue;
		}
		// Verify the match is a complete word (not a prefix of a longer word)
		const nextIndex = state.index + type.length;
		if (nextIndex < state.length) {
			const next = state.pattern[nextIndex];
			if (!isWhitespace(next) && next !== "," && next !== "}") {
				continue;
			}
		}
		argType = type;
		state.index += type.length;
		break;
	}

	if (argType == null) {
		throwExpected(state, _types.join(", "), null, null);
	}

	skipWhitespace(state);
	return argType;
}

function parseArgument(state: ParserState): ParserArgument {
	const p = state.pattern;

	if (p[state.index] === "#") {
		++state.index;
		return {
			id: "#",
			offset: 0,
			styleFormat: null,
			subMsgFormat: null,
			type: "number"
		};
	}
	++state.index;
	const id = parseArgId(state);
	let c = p[state.index];
	if (c === "}") {
		++state.index;
		return {
			id: id,
			offset: 0,
			styleFormat: null,
			subMsgFormat: null,
			type: "value"
		};
	}

	if (c !== ",") {
		throwExpected(state, ",", null, null);
	}

	++state.index;
	const type = parseArgType(state);
	c = p[state.index];

	const ret: ParserArgument = {
		id: id,
		offset: 0,
		styleFormat: null,
		subMsgFormat: null,
		type: type
	};

	if (c === "}") {
		if (type === "plural" || type === "selectordinal" || type === "select") {
			throwExpected(state, `${type} message options`, null, null);
		}
		++state.index;
		return ret;
	}

	if (c !== ",") {
		throwExpected(state, ",", null, null);
	}

	++state.index;

	switch (type) {
		case "plural":
		case "selectordinal": {
			ret.offset = parsePluralOffset(state);
			ret.subMsgFormat = parseSubMessages(state, type);
			break;
		}
		case "select": {
			ret.subMsgFormat = parseSubMessages(state, type);
			break;
		}
		default: {
			ret.styleFormat = parseSimpleFormat(state);
			break;
		}
	}

	c = p[state.index];
	if (c !== "}") {
		throwExpected(state, "}", null, null);
	}

	++state.index;
	return ret;
}

function parseMessageInternal(state: ParserState, parentType: ParserArgumentType): ParserArgument[] {
	let text = parseText(state, parentType);
	const elements: ParserArgument[] = [];

	if (!isNullOrEmpty(text)) {
		elements.push({
			id: text,
			offset: 0,
			styleFormat: null,
			subMsgFormat: null,
			type: "text"
		});
	}

	while (state.index < state.length) {
		if (state.pattern[state.index] === "}") {
			if (parentType === "#") {
				throwExpected(state, null, null, null);
			}
			break;
		}
		elements.push(parseArgument(state)); //, parentType
		text = parseText(state, parentType);
		if (!isNullOrEmpty(text)) {
			elements.push({
				id: text,
				offset: 0,
				styleFormat: null,
				subMsgFormat: null,
				type: "text"
			});
		}
	}

	return elements;
}

function parsePluralOffset(state: ParserState): number {
	skipWhitespace(state);

	if (!containsAtIndex(state, "offset:")) {
		return 0;
	}

	state.index += 7; //"offset:".Length == 7
	skipWhitespace(state);

	let start = state.index;
	while (state.index < state.length && isDigit(state.pattern[state.index])) {
		++state.index;
	}

	if (start === state.index) {
		throwExpected(state, "offset number", null, null);
	}

	const offset = parseInt(substring(state, start, state.index - start), 10);
	skipWhitespace(state);
	return offset;
}

function parseSelector(state: ParserState): string {
	let builder = "";

	while (state.index < state.length) {
		let c = state.pattern[state.index];
		if (c === "}" || c === ",") {
			throwExpected(state, "{", null, null);
		}
		if (c === "{" || isWhitespace(c)) {
			break;
		}
		builder += c;
		++state.index;
	}

	if (builder.length === 0) {
		throwExpected(state, "selector", null, null);
	}

	skipWhitespace(state);
	return builder;
}

function parseSimpleFormat(state: ParserState): string {
	skipWhitespace(state);

	let style = parseText(state, "style");
	if (isNullOrEmpty(style)) {
		throwExpected(state, "argument style name", null, null);
	}

	skipWhitespace(state);
	return style;
}

function parseSubMessage(state: ParserState, parentType: ParserArgumentType): ParserArgument[] {
	let c = state.pattern[state.index];
	if (c !== "{") {
		throwExpected(state, "{", null, null);
	}
	++state.index;
	const message = parseMessageInternal(state, parentType);
	c = state.pattern[state.index];
	if (c !== "}") {
		throwExpected(state, "}", null, null);
	}
	++state.index;
	return message;
}

function parseSubMessages(state: ParserState, parentType: ParserArgumentType): Record<string, ParserArgument[]> {
	skipWhitespace(state);

	const options: Record<string, ParserArgument[]> = {};
	let hasSubs = false;

	while (state.index < state.length && state.pattern[state.index] !== "}") {
		const selector = parseSelector(state);
		skipWhitespace(state);
		options[selector] = parseSubMessage(state, parentType);
		hasSubs = true;
		skipWhitespace(state);
	}

	if (!hasSubs) {
		throwExpected(state, `${parentType} message options`, null, null);
	}

	if (options["other"] == null) {
		//does not have another selector
		throwExpected(state, null, null, `\"other\" option must be specified in ${parentType}`);
	}
	return options;
}

function parseText(state: ParserState, parentType: ParserArgumentType): string | null {
	const isHashSpecial = parentType === "plural" || parentType === "selectordinal";
	const isArgStyle = parentType === "style";
	let builder = "";
	const p = state.pattern;

	while (state.index < state.length) {
		let c = p[state.index];
		if (c === "{" || c === "}" || isHashSpecial && c === "#" || isArgStyle && isWhitespace(c)) {
			break;
		}
		if (c === "'") {
			++state.index;
			c = p[state.index];
			if (c === "'") {
				builder += c;
				++state.index;
			} else if (c === "{" || c === "}" || isHashSpecial && c === "#" || isArgStyle) {
				builder += c;
				while (++state.index < state.length) {
					c = p[state.index];
					if (c === "'" && p[state.index + 1] === "'") {
						builder += c;
						++state.index;
					} else if (c === "'") {
						++state.index;
						break;
					} else {
						builder += c;
					}
				}
			} else {
				builder += "'";
			}
		} else {
			builder += c;
			++state.index;
		}
	}
	return builder.length !== 0 ? builder : null;
}

function skipWhitespace(state: ParserState): void {
	while (state.index < state.length && isWhitespace(state.pattern[state.index])) {
		++state.index;
	}
}

function substring(state: ParserState, index: number, length: number): string {
	return index + length > state.length ? state.pattern.substring(index) : state.pattern.substring(index, index + length);
}

function throwExpected(state: ParserState, expected: string | null, found: string | null, message: string | null): never {
	const lines = substring(state, 0, state.index).split(_splitRegex);
	const offset = state.index;
	const line = lines.length;
	const column = lines[line - 1].length;

	if (found == null) {
		if (state.index >= state.length) {
			found = "end of message";

		} else {
			found = state.pattern[state.index];
			while (++state.index < state.length) {
				let c = state.pattern[state.index];
				if (!isDigit(c) && c.toUpperCase() === c.toLowerCase()) {
					break;
				}
				found += c;
			}
		}
	}

	message ??= errorMessage(expected, found);
	message += ` in ${state.pattern.replace(_splitRegex, "\n")}`;
	throw new MessageParserError(message, expected ?? "none", found, offset, line, column);
}


export default function parseMessage(pattern: string): ParserArgument[] {
	return parseMessageInternal({
		length: pattern.length,
		pattern: pattern,
		index: 0
	}, "#");
}
